/**
 * Local validation dataset: the newest PRICING_HISTORY_LIMIT
 * PricingValidationRecords in localStorage (no database, no server).
 *
 * A record holds statistics only — never a file name, PDF text, translated
 * text, title, author, API key or any personal data. Developer Mode lists
 * the records, shows a plain summary (sample count, mean cost error, worst
 * under-estimate, mean / minimum margin, LOSS count) and exports a CSV with
 * Traditional Chinese headers. Nothing here recommends or changes a price.
 */

import { PRICING_HISTORY_LIMIT, PRICING_HISTORY_STORAGE_KEY } from './config';
import type { PricingSafetyResult, PricingValidation } from './validation';

export interface PricingValidationRecord {
  timestamp: string;
  pricingVersion: string;
  scopeMode: 'all' | 'chapters' | 'pages';
  selectedPages: number[];
  billableChars: number;
  billableUnits: number;

  bodyUnits: number;
  headingUnits: number;
  captionUnits: number;
  footnoteUnits: number;
  tableCellUnits: number;
  figureUnits: number;
  structuredLabelUnits: number;
  sidebarUnits: number;
  otherUnits: number;

  suggestedPrice: number;

  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedApiCostTwd: number;

  actualInputTokens: number;
  actualCachedInputTokens: number;
  actualOutputTokens: number;
  actualProcessedTokens: number;
  actualApiCostTwd: number;

  estimatedPaymentFee: number;
  grossProfit: number;
  grossMarginRate: number;

  tokenEstimateErrorRate: number;
  costEstimateErrorRate: number;
  safetyResult: PricingSafetyResult;
  /** Units served from the translation cache (explains a low actual cost). */
  cachedUnits: number;
}

/** Field names a record may never contain (guarded by the tests). */
export const FORBIDDEN_RECORD_FIELDS = ['fileName', 'filename', 'text', 'source', 'translation', 'title', 'author', 'apiKey', 'email'] as const;

export interface HistoryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStorage(): HistoryStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : Number.NaN;
}

/** Build the record from a validation and the quote's unit breakdown; copies numbers only. */
export function toRecord(
  v: PricingValidation,
  breakdown: {
    timestamp: string;
    selectedPages: readonly number[];
    bodyUnits: number;
    headingUnits: number;
    captionUnits: number;
    footnoteUnits: number;
    tableCellUnits: number;
    figureUnits: number;
    structuredLabelUnits: number;
    sidebarUnits: number;
    otherUnits: number;
  },
): PricingValidationRecord {
  return {
    timestamp: breakdown.timestamp,
    pricingVersion: v.pricingVersion,
    scopeMode: v.scopeMode,
    selectedPages: [...breakdown.selectedPages],
    billableChars: v.billableChars,
    billableUnits: v.billableUnits,
    bodyUnits: breakdown.bodyUnits,
    headingUnits: breakdown.headingUnits,
    captionUnits: breakdown.captionUnits,
    footnoteUnits: breakdown.footnoteUnits,
    tableCellUnits: breakdown.tableCellUnits,
    figureUnits: breakdown.figureUnits,
    structuredLabelUnits: breakdown.structuredLabelUnits,
    sidebarUnits: breakdown.sidebarUnits,
    otherUnits: breakdown.otherUnits,
    suggestedPrice: v.suggestedPrice,
    estimatedInputTokens: v.estimatedInputTokens,
    estimatedOutputTokens: v.estimatedOutputTokens,
    estimatedApiCostTwd: v.estimatedApiCostTwd,
    actualInputTokens: v.actual.actualInputTokens,
    actualCachedInputTokens: v.actual.actualCachedInputTokens,
    actualOutputTokens: v.actual.actualOutputTokens,
    actualProcessedTokens: v.actual.actualProcessedTokens,
    actualApiCostTwd: v.actual.actualApiCostTwd,
    estimatedPaymentFee: v.estimatedPaymentFee,
    grossProfit: v.grossProfit,
    grossMarginRate: v.grossMarginRate,
    tokenEstimateErrorRate: v.tokenEstimateErrorRate,
    costEstimateErrorRate: v.costEstimateErrorRate,
    safetyResult: v.safetyResult,
    cachedUnits: v.delivery?.cachedUnits ?? 0,
  };
}

function isRecord(x: unknown): x is PricingValidationRecord {
  if (!x || typeof x !== 'object') return false;
  const r = x as Record<string, unknown>;
  return typeof r.timestamp === 'string' && typeof r.pricingVersion === 'string' && typeof r.suggestedPrice === 'number';
}

export class PricingHistory {
  private readonly storage: HistoryStorage | null;
  private readonly key: string;
  private readonly limit: number;

  constructor(storage: HistoryStorage | null = defaultStorage(), key = PRICING_HISTORY_STORAGE_KEY, limit = PRICING_HISTORY_LIMIT) {
    this.storage = storage;
    this.key = key;
    this.limit = limit;
  }

  /** Newest first. Unreadable storage yields an empty list, never an exception. */
  list(): PricingValidationRecord[] {
    if (!this.storage) return [];
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isRecord);
    } catch {
      return [];
    }
  }

  /** Prepend and keep the newest `limit`. Returns the stored list. */
  add(record: PricingValidationRecord): PricingValidationRecord[] {
    const next = [record, ...this.list()].slice(0, this.limit);
    this.write(next);
    return next;
  }

  clear(): void {
    try {
      this.storage?.removeItem(this.key);
    } catch {
      /* storage unavailable */
    }
  }

  private write(records: PricingValidationRecord[]): void {
    try {
      this.storage?.setItem(this.key, JSON.stringify(records));
    } catch (err) {
      console.warn('[Pricing] validation history not saved', err);
    }
  }
}

// ---------------------------------------------------------------------------
// Summary (statistics only; no recommendation)
// ---------------------------------------------------------------------------

export interface HistorySummary {
  samples: number;
  meanCostErrorRate: number;
  /** Largest positive cost error (actual above estimate). 0 when nothing was under-estimated. */
  maxUnderestimateRate: number;
  meanGrossMarginRate: number;
  minGrossMarginRate: number;
  lossCount: number;
  warningCount: number;
  safeCount: number;
}

export function summarizeHistory(records: readonly PricingValidationRecord[]): HistorySummary {
  const n = records.length;
  const finite = (xs: number[]) => xs.filter((x) => Number.isFinite(x));
  const errors = finite(records.map((r) => num(r.costEstimateErrorRate)));
  const margins = finite(records.map((r) => num(r.grossMarginRate)));
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : Number.NaN);
  return {
    samples: n,
    meanCostErrorRate: mean(errors),
    maxUnderestimateRate: errors.length ? Math.max(0, ...errors) : Number.NaN,
    meanGrossMarginRate: mean(margins),
    minGrossMarginRate: margins.length ? Math.min(...margins) : Number.NaN,
    lossCount: records.filter((r) => r.safetyResult === 'LOSS').length,
    warningCount: records.filter((r) => r.safetyResult === 'WARNING').length,
    safeCount: records.filter((r) => r.safetyResult === 'SAFE').length,
  };
}

// ---------------------------------------------------------------------------
// CSV (Traditional Chinese headers, numbers only)
// ---------------------------------------------------------------------------

const CSV_COLUMNS: readonly [string, (r: PricingValidationRecord) => string | number][] = [
  ['時間', (r) => r.timestamp],
  ['定價版本', (r) => r.pricingVersion],
  ['範圍模式', (r) => r.scopeMode],
  ['選取頁數', (r) => r.selectedPages.length],
  ['選取頁面', (r) => r.selectedPages.join(' ')],
  ['可翻譯字元', (r) => r.billableChars],
  ['可翻譯單元', (r) => r.billableUnits],
  ['內文單元', (r) => r.bodyUnits],
  ['標題單元', (r) => r.headingUnits],
  ['圖表標題單元', (r) => r.captionUnits],
  ['註腳單元', (r) => r.footnoteUnits],
  ['表格儲存格單元', (r) => r.tableCellUnits],
  ['圖內文字單元', (r) => r.figureUnits],
  ['結構式標籤單元', (r) => r.structuredLabelUnits],
  ['側欄單元', (r) => r.sidebarUnits],
  ['其他單元', (r) => r.otherUnits],
  ['建議售價', (r) => r.suggestedPrice],
  ['預估輸入Token', (r) => r.estimatedInputTokens],
  ['預估輸出Token', (r) => r.estimatedOutputTokens],
  ['預估API成本', (r) => r.estimatedApiCostTwd],
  ['實際輸入Token', (r) => r.actualInputTokens],
  ['實際快取輸入Token', (r) => r.actualCachedInputTokens],
  ['實際輸出Token', (r) => r.actualOutputTokens],
  ['實際處理Token', (r) => r.actualProcessedTokens],
  ['實際API成本', (r) => r.actualApiCostTwd],
  ['預估付款手續費', (r) => r.estimatedPaymentFee],
  ['毛利', (r) => r.grossProfit],
  ['毛利率', (r) => r.grossMarginRate],
  ['Token估算誤差', (r) => r.tokenEstimateErrorRate],
  ['成本估算誤差', (r) => r.costEstimateErrorRate],
  ['快取命中單元', (r) => r.cachedUnits],
  ['定價結果', (r) => r.safetyResult],
];

function csvCell(v: string | number): string {
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  // A cell that starts with = + - @ could be read as a formula by Excel; neutralize it.
  const s = /^[=+\-@]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** UTF-8 CSV with BOM (Excel opens it as Traditional Chinese directly). */
export function historyToCsv(records: readonly PricingValidationRecord[]): string {
  const header = CSV_COLUMNS.map(([h]) => csvCell(h)).join(',');
  const rows = records.map((r) => CSV_COLUMNS.map(([, f]) => csvCell(f(r))).join(','));
  return `﻿${[header, ...rows].join('\r\n')}\r\n`;
}
